import { injectable } from "inversify";
import ErrorEntity from "../../domain/entities/error.entity";
import KeyWordLocalization from "../../presentation/utils/KeyWordLocalization";
import { console } from "fp-ts";

@injectable()
class RestApi {
  public static KEY_HOST_URL = "host_url_variable";
  public static KEY_DOMAIN_URL = "domain_url_variable";
  public KEY_TOKEN = "token_api_key";
  public url = "";

  private domainsUrl: { [key: string]: string } = {
    "tnoutsourcing.com": process.env.REACT_APP_API_DOMAIN_TNOUTSOURCING || "",
    "blackpool.la": process.env.REACT_APP_API_DOMAIN_BLACKPOOL || "",
    "test.com": process.env.REACT_APP_API_DOMAIN_TEST || "",
  }

  constructor() {
    this.getUrl();
  }

  public setDomain = (domain: string) => {
    this.url = this.domainsUrl[domain] ?? "";
    window.localStorage.setItem(RestApi.KEY_HOST_URL, this.url);
    window.localStorage.setItem(RestApi.KEY_DOMAIN_URL, domain);
    return this.url;
  }

  public getUrl = () => {
    if (this.url == "") {
      const domain = window.localStorage.getItem(RestApi.KEY_DOMAIN_URL);
      if (domain) {
        this.url = this.domainsUrl[domain] ?? "";
        window.localStorage.setItem(RestApi.KEY_HOST_URL, this.url);
      }
    }
    return this.url;
  }

  public getToken = () => window.localStorage.getItem(this.KEY_TOKEN) ? `Bearer ${window.localStorage.getItem(this.KEY_TOKEN)}` : "";

  public setToken = (token: string) => {
    window.localStorage.setItem(this.KEY_TOKEN, token);
  };

  public removeToken = () => window.localStorage.removeItem(this.KEY_TOKEN);

  public _getHeaders = (headers?: { contentType?: string, responseType?: string, }) => {
    return {
      'Content-Type': headers?.contentType ?? 'application/x-www-form-urlencoded',
      'Authorization': this.getToken(),
    };
  }

  public _parseError = async (response: Response): Promise<ErrorEntity> => {
    try {
      const error = await response.json();

      if (Array.isArray(error.detail)) {
        const formatted = error.detail
          .map((e: any) => `${e.loc?.join('.')} - ${e.msg}`)
          .join('\n');
        return {
          code: "ValidationError",
          message: formatted,
        };
      }

      return {
        code: "ServerError",
        message: JSON.stringify(error.detail ?? error),
      };
    } catch (err) {
      try {
        const errorAsText = await response.text();
        return {
          code: "TextError",
          message: errorAsText,
        };
      } catch (finalErr) {
        return {
          code: "UnknownError",
          message: finalErr?.toString(),
        };
      }
    }
  };



  public get = (relativeUrl: string, params?: {
    contentType?: string, responseType?: string,
  }): Promise<any> => new Promise((resolve, reject) => {
    fetch(this.url + relativeUrl, {
      method: 'GET',
      // mode: 'no-cors',
      headers: this._getHeaders(params),
    }).then(async response => {
      if (!response.ok || response.status >= 400) {
        const errorParsed = await this._parseError(response);
        return reject(errorParsed);
      }
      if (params?.responseType == "blob") {
        const blob = await response.blob();
        return resolve(blob);
      }
      const ts = await response.text();
      try {
        const js = await JSON.parse(ts);
        return resolve(js);

      } catch (error) {
        return resolve(ts);
      }
    }).catch((error) => reject(error));
  });


  public post = (
    relativeUrl: string,
    body: any,
    params?: {
      noCors?: boolean;
      contentType?: string;
    }
  ): Promise<any> => new Promise((resolve, reject) => {
    const isFormData = body instanceof FormData;
    const headers = this._getHeaders(params);

    if (isFormData) {
      delete (headers as any)['Content-Type'];
    } else if (params?.contentType) {
      (headers as any)['Content-Type'] = params.contentType;
    }

    fetch(this.url + relativeUrl, {
      method: 'POST',
      mode: params?.noCors === true ? 'no-cors' : undefined,
      headers,
      body: isFormData ? body : (params?.contentType === 'application/x-www-form-urlencoded' ? body : JSON.stringify(body)),
    })
      .then(async (response) => {
        if (!response.ok || response.status >= 400) {
          const errorParsed = await this._parseError(response);
          return reject(errorParsed);
        }

        const ts = await response.text();
        try {
          const js = JSON.parse(ts);
          return resolve(js);
        } catch (error) {
          return resolve(ts);
        }
      })
      .catch((error) => reject(error));
  });


  public put = (
    relativeUrl: string,
    body: any,
    params?: {
      contentType?: string;
    }
  ): Promise<any> =>
    new Promise((resolve, reject) => {
      const isFormData = body instanceof FormData;
      const headers = this._getHeaders(params);

      if (isFormData) {
        delete (headers as any)["Content-Type"];
      }

      fetch(this.url + relativeUrl, {
        method: "PUT",
        headers,
        body: isFormData ? body : JSON.stringify(body),
      })
        .then(async (response) => {
          if (!response.ok || response.status >= 400) {
            const errorParsed = await this._parseError(response);
            return reject(errorParsed);
          }

          const ts = await response.text();
          try {
            const js = await JSON.parse(ts);
            return resolve(js);
          } catch (error) {
            return resolve(ts);
          }
        })
        .catch((error) => reject(error));
    });


  public remove = (relativeUrl: string, params?: {
    contentType?: string
  }): Promise<any> => new Promise((resolve, reject) => {
    fetch(this.url + relativeUrl, {
      method: 'DELETE',
      headers: this._getHeaders(params),
    }).then(async response => {
      if (!response.ok || response.status >= 400) {
        const errorParsed = await this._parseError(response);
        return reject(errorParsed);
      }
      const ts = await response.text();
      try {
        const js = await JSON.parse(ts);
        return resolve(js);

      } catch (error) {
        return resolve(ts);
      }
    }).catch((error) => reject(error));
  });

  
  public deleteAgentFile = (
    agentId: string,
    fileId: string,
    assistantId: string,
    vectorStoreId: string
  ): Promise<any> => {
    const query = new URLSearchParams({
      assistant_id: assistantId,
      vector_store_id: vectorStoreId,
    }).toString();
  
    const url = `/agent/${agentId}/files/${fileId}?${query}`;
    return this.remove(url);
  };
}
// axios.defaults.headers.post['Access-Control-Allow-Origin'] = '*';




export default RestApi;